import { useState } from 'react'
import { AlertCircle, Bell, BellOff, BellRing, KeyRound, Mail, ShieldCheck, UserRound } from 'lucide-react'
import { Callout, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useAuthStore } from '@/stores/authStore'
import { toApiProblem } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { pushSupport, useCurrentSubscription, usePushMutations } from '../api'

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('')
}

/** `HR_MANAGER` -> `Hr manager` */
function humanise(role: string) {
  const spaced = role.replace(/^ROLE_/, '').replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * The signed-in person's own settings.
 *
 * <p>Push lives here rather than in an administration screen because it is a per-browser
 * decision, not a per-user one: subscribing registers <em>this</em> device, and the same
 * person on a phone and a laptop has two independent answers. The screen says which device
 * it is talking about for that reason.
 */
export function ProfilePage() {
  useBreadcrumb()
  const user = useAuthStore((state) => state.user)
  const permissions = useAuthStore((state) => state.permissions)
  const subscription = useCurrentSubscription()
  const { subscribe, unsubscribe } = usePushMutations()
  const [error, setError] = useState<string | null>(null)

  const support = pushSupport()
  const subscribed = subscription.data != null
  const busy = subscribe.isPending || unsubscribe.isPending
  const roles = user?.roles ?? []

  async function toggle() {
    setError(null)
    try {
      if (subscribed) await unsubscribe.mutateAsync()
      else await subscribe.mutateAsync()
    } catch (caught) {
      // Two sources of failure with different shapes: the browser refusing permission
      // throws an Error, and the server refusing the call returns a problem document.
      setError(
        toApiProblem(caught)?.detail
          ?? (caught instanceof Error && caught.message
            ? caught.message
            : 'That could not be changed'),
      )
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Your profile" subtitle="Who you are signed in as, and how this device reaches you." />

      <Card className="overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-[var(--color-primary)] to-[color-mix(in_srgb,var(--color-primary)_55%,var(--color-info))]" />
        <div className="flex flex-wrap items-end gap-4 px-6 pb-6">
          <span className="-mt-10 grid size-20 shrink-0 place-items-center rounded-2xl border-4 border-[var(--color-card)] bg-[var(--color-primary-soft)] text-2xl font-semibold text-[var(--color-primary)] shadow-sm">
            {user?.fullName ? initials(user.fullName) : <UserRound className="size-8" aria-hidden="true" />}
          </span>
          <div className="min-w-0 flex-1 pt-3">
            <h2 className="truncate text-lg font-semibold text-[var(--color-heading)]">{user?.fullName ?? '—'}</h2>
            <p className="flex items-center gap-1.5 text-sm text-[var(--color-muted)]">
              <Mail className="size-3.5" aria-hidden="true" />
              {user?.email ?? '—'}
            </p>
          </div>
        </div>

        <dl className="grid border-t border-[var(--color-border)] sm:grid-cols-2">
          <div className="px-6 py-4 sm:border-e sm:border-[var(--color-border)]">
            <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
              <KeyRound className="size-3.5" aria-hidden="true" /> Roles
            </dt>
            <dd className="mt-2 flex flex-wrap gap-1.5">
              {roles.length > 0
                ? roles.map((role) => (
                  <span
                    key={role}
                    className="rounded-full bg-[var(--color-primary-soft)] px-2.5 py-0.5 text-xs font-medium text-[var(--color-primary)]"
                  >
                    {humanise(role)}
                  </span>
                ))
                : <span className="text-sm text-[var(--color-muted)]">—</span>}
            </dd>
          </div>
          <div className="border-t border-[var(--color-border)] px-6 py-4 sm:border-t-0">
            <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
              <ShieldCheck className="size-3.5" aria-hidden="true" /> Permissions
            </dt>
            <dd className="mt-1.5 text-sm text-[var(--color-body)]">
              <span className="text-xl font-semibold tabular-nums text-[var(--color-heading)]">{permissions.size}</span>{' '}
              granted through your roles
            </dd>
          </div>
        </dl>
      </Card>

      <Card className="p-6">
        <div className="flex flex-wrap items-start gap-4">
          <span
            className={cn(
              'grid size-11 shrink-0 place-items-center rounded-xl',
              subscribed
                ? 'bg-[var(--color-tint-green)] text-[var(--color-success)]'
                : 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
            )}
          >
            {subscribed ? <BellRing className="size-5" aria-hidden="true" /> : <Bell className="size-5" aria-hidden="true" />}
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 className="text-base font-semibold text-[var(--color-heading)]">Notifications on this device</h2>
            <p className="max-w-2xl text-sm text-[var(--color-muted)]">
              Approvals waiting on you, and documents about to expire, delivered to this browser
              even when the app is closed. This is a per-device choice: turning it on here does
              nothing for your phone.
            </p>
          </div>
        </div>

        <div className="mt-5">
          {!support.supported && (
            <Callout tone="warning" icon={BellOff}>
              {support.reason ?? 'This browser cannot receive push notifications.'}
            </Callout>
          )}

          {support.supported && subscription.isPending && <Skeleton className="h-14 w-full" />}

          {support.supported && !subscription.isPending && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-page)] px-4 py-3">
              <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]" data-testid="push-state">
                <span
                  aria-hidden="true"
                  className={cn(
                    'size-2.5 rounded-full',
                    subscribed ? 'bg-[var(--color-success)] ring-4 ring-[var(--color-success)]/20' : 'bg-[var(--color-muted)]',
                  )}
                />
                {subscribed
                  ? 'This device is subscribed.'
                  : 'This device is not subscribed.'}
              </p>
              <Button
                type="button"
                variant={subscribed ? 'outline' : 'primary'}
                disabled={busy}
                onClick={() => void toggle()}
              >
                {subscribed ? (
                  <><BellOff aria-hidden="true" /> Turn off on this device</>
                ) : (
                  <><Bell aria-hidden="true" /> Turn on for this device</>
                )}
              </Button>
            </div>
          )}

          {error && (
            <p role="alert" className="mt-3 flex items-center gap-2 text-sm text-[var(--color-danger)]">
              <AlertCircle className="size-4 shrink-0" aria-hidden="true" /> {error}
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}
